// frontend/src/components/ProfileFlyout.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-10
// Updated:      2026-09-24
//
// Description:
//   Compact authenticated profile flyout.
//
// 2026-09-24:
//   • Removes the inline Manage Profile accordion/editor.
//   • Removes Browse as guest and Add Profile actions and associated state/handlers.
//   • Manage Profile Settings delegates to TopNav, which opens ManageUserModal.
//   • Logout uses the canonical AuthContext logout implementation.
//   • Loads avatars directly from the stored /static URL so normal browser caching can be used.
//   • Cache-busts only after a successful avatar upload; upload behaviour remains authenticated.
//   • Matches the ManageUserModal avatar edit affordance exactly: transparent, borderless pencil emoji.
//   • Keeps the same component for desktop and mobile.
// 2026-11-02 GMT: Context-free Create Expense entry point using shared Layout modal.

import React, { useEffect, useRef, useState } from "react";
import {
  FaCheckCircle,
  FaReceipt,
  FaSignOutAlt,
  FaTimes,
  FaUserCog,
} from "react-icons/fa";

import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const ProfileFlyout = ({ mobile = false, onClose, onManageProfile }) => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();

  const fileInputRef = useRef(null);

  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || null);
  const [avatarTimestamp, setAvatarTimestamp] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAvatarUrl(user?.avatar_url || null);
    setAvatarTimestamp(0);
  }, [user?.id, user?.avatar_url]);

  const avatarPreview = (() => {
    if (!avatarUrl) return null;
    if (!avatarTimestamp) return avatarUrl;

    const separator = avatarUrl.includes("?") ? "&" : "?";
    return `${avatarUrl}${separator}v=${avatarTimestamp}`;
  })();

  const handleAvatarClick = () => {
    if (!busy) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith("image/") || !user?.id) return;

    const payload = new FormData();
    payload.append("file", file);

    setBusy(true);
    try {
      const res = await axiosInstance.post(`/users/${user.id}/avatar`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setAvatarUrl(res.data?.avatar_url || user.avatar_url || null);
      setAvatarTimestamp(Date.now());
    } catch (err) {
      console.error("[Avatar Upload Error]:", err.response?.data || err);
      alert("Avatar upload failed.");
    } finally {
      setBusy(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleManageProfile = () => {
    onManageProfile?.();
  };

  const handleCreateExpense = () => {
    onClose?.();
    window.dispatchEvent(new CustomEvent("vision:open-expense-create"));
  };

  const handleLogout = async () => {
    onClose?.();
    await logout();
  };

  if (!user) return null;

  const actionColour = theme.button_action?.normal || "#172d41";
  const actionHover = theme.button_action?.hover || actionColour;

  return (
    <div
      className={mobile ? undefined : "absolute right-6 top-24 z-[2000]"}
      style={{
        position: mobile ? "relative" : undefined,
        backgroundColor: "#fff",
        borderRadius: mobile ? "12px 0 0 12px" : "12px",
        boxShadow: mobile
          ? "-8px 0 24px rgba(0, 0, 0, 0.22)"
          : "0 6px 18px rgba(0, 0, 0, 0.1)",
        border: "1px solid #e5e7eb",
        padding: mobile ? "1rem" : "1.25rem",
        width: mobile ? "100%" : "20rem",
        height: mobile ? "100dvh" : undefined,
        maxHeight: mobile ? "100dvh" : undefined,
        overflowY: mobile ? "auto" : undefined,
        overflowX: "hidden",
        boxSizing: "border-box",
        WebkitOverflowScrolling: mobile ? "touch" : undefined,
      }}
    >
      {mobile && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "1rem",
            paddingBottom: "0.75rem",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div style={{ fontSize: "1.1rem", fontWeight: 700 }}>Profile</div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close profile"
            title="Close profile"
            style={{
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              border: "none",
              borderRadius: "8px",
              background: "transparent",
              color: theme.accent_color || "#0077cc",
              cursor: "pointer",
              touchAction: "manipulation",
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.9rem",
          minWidth: 0,
        }}
      >
        <div
          style={{
            position: "relative",
            width: "68px",
            height: "68px",
            flex: "0 0 68px",
          }}
        >
          {avatarPreview ? (
            <img
              src={avatarPreview}
              alt="User avatar"
              loading="eager"
              decoding="async"
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #d1d5db",
              }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                background: "#d1d5db",
                color: "#fff",
                fontWeight: 800,
                fontSize: "1.15rem",
              }}
            >
              {user.first_name?.[0] || ""}
              {user.last_name?.[0] || ""}
            </div>
          )}

          <button
            type="button"
            onClick={handleAvatarClick}
            disabled={busy}
            className="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center bg-transparent p-0 text-base leading-none transition-opacity hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-btnActionNormal"
            title="Change avatar"
            aria-label="Change avatar"
          >
            ✏️
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 800,
              fontSize: "1rem",
              overflowWrap: "anywhere",
            }}
          >
            {[user.first_name, user.last_name].filter(Boolean).join(" ") ||
              user.username}
          </div>
          <div
            style={{
              marginTop: "0.15rem",
              color: "#6b7280",
              fontSize: "0.82rem",
              overflowWrap: "anywhere",
            }}
          >
            {user.email}
          </div>
          <div
            style={{
              marginTop: "0.35rem",
              display: "flex",
              alignItems: "center",
              gap: "0.35rem",
              color: "#6b7280",
              fontSize: "0.76rem",
            }}
          >
            <FaCheckCircle size={12} />
            <span>Sync is on</span>
          </div>
        </div>
      </div>

      <div style={{ borderTop: "1px solid #e5e7eb", margin: "1.25rem 0" }} />

      <button
        type="button"
        onClick={handleManageProfile}
        style={{
          width: "100%",
          minHeight: "44px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.55rem",
          padding: "0.65rem 0.8rem",
          border: "none",
          borderRadius: "7px",
          background: actionColour,
          color: "#fff",
          fontWeight: 700,
          cursor: "pointer",
          touchAction: "manipulation",
        }}
        onMouseEnter={(event) => {
          event.currentTarget.style.backgroundColor = actionHover;
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.backgroundColor = actionColour;
        }}
      >
        <FaUserCog />
        <span>Manage Profile Settings</span>
      </button>

      <button
        type="button"
        onClick={handleCreateExpense}
        className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick"
        style={{
          width: "100%", minHeight: "44px", display: "flex", alignItems: "center",
          justifyContent: "center", gap: "0.55rem", padding: "0.65rem 0.8rem",
          border: "none", borderRadius: "7px", color: "#fff", fontWeight: 700,
          cursor: "pointer", touchAction: "manipulation", marginTop: 10,
        }}
      >
        <FaReceipt />
        <span>Create Expense</span>
      </button>

      <div style={{ borderTop: "1px solid #e5e7eb", margin: "1.25rem 0" }} />

      <button
        type="button"
        onClick={handleLogout}
        style={{
          width: "100%",
          minHeight: "44px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.55rem",
          padding: "0.65rem 0.8rem",
          border: "none",
          borderRadius: "7px",
          background: actionColour,
          color: "#fff",
          fontWeight: 700,
          cursor: "pointer",
          touchAction: "manipulation",
        }}
        onMouseEnter={(event) => {
          event.currentTarget.style.backgroundColor = actionHover;
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.backgroundColor = actionColour;
        }}
      >
        <FaSignOutAlt />
        <span>Logout</span>
      </button>
    </div>
  );
};

export default ProfileFlyout;
